import { getApiEnvironment } from '~/utils/common'

interface ApiDomains {
  MANGO_API: string
  JWT_AUTH: string
  CHAT_RTM: string
  MUSIC_SERVICE: string
  GATEWAY: string
  WEB_APP: string
}

interface ApiDomainsAccessor {
  JWT_AUTH: () => string
  MANGO_API: () => string
  CHAT_RTM: () => string
  MUSIC_SERVICE: () => string
  GATEWAY: () => string
  WEB_APP: () => string
}

const localApiBaseUrl =
  (import.meta.env.VITE_MANGO_API as string | undefined)?.trim() ||
  'http://localhost:8001'

const localMusicServiceUrl =
  (import.meta.env.VITE_MUSIC_SERVICE as string | undefined)?.trim() ||
  'http://localhost:8002'

/**
 * There is no local api-gateway, so local dev points at the staging gateway by
 * default. Set VITE_GATEWAY to target a locally running gateway/service.
 */
const localGatewayUrl =
  (import.meta.env.VITE_GATEWAY as string | undefined)?.trim() ||
  'https://gateway-staging.mangovoice.com'

const localWebAppUrl =
  (import.meta.env.VITE_WEB_APP_URL as string | undefined)?.trim() ||
  'http://localhost:3000'

export const localDomains: ApiDomains = {
  MANGO_API: localApiBaseUrl,
  JWT_AUTH: localApiBaseUrl,
  CHAT_RTM: 'wss://wss-staging.mangovoice.com:4000',
  MUSIC_SERVICE: localMusicServiceUrl,
  GATEWAY: localGatewayUrl,
  WEB_APP: localWebAppUrl,
}

export const stagingDomains: ApiDomains = {
  MANGO_API: 'https://api-staging.mangovoice.com',
  JWT_AUTH: 'https://api-staging.mangovoice.com',
  CHAT_RTM: 'wss://wss-staging.mangovoice.com:4000',
  MUSIC_SERVICE: 'https://music-service-staging.mangovoice.com',
  GATEWAY: 'https://gateway-staging.mangovoice.com',
  WEB_APP: 'https://app-staging.mangovoice.com',
}

export const productionDomains: ApiDomains = {
  MANGO_API: 'https://api.mangovoice.com',
  JWT_AUTH: 'https://api.mangovoice.com',
  CHAT_RTM: 'wss://wss.mangovoice.com:4000',
  MUSIC_SERVICE: 'https://music-service.mangovoice.com',
  // `customer-api` is the customer-facing name for the api-gateway, serving the
  // same routes as the older `gateway.*` hosts. Production only — staging keeps
  // gateway-staging.mangovoice.com.
  GATEWAY: 'https://customer-api.mangovoice.com',
  WEB_APP: 'https://app.mangovoice.com',
}

export const productionUKDomains: ApiDomains = {
  MANGO_API: 'https://api.mangovoice.co.uk',
  JWT_AUTH: 'https://api.mangovoice.co.uk',
  CHAT_RTM: 'wss://wss.mangovoice.co.uk:4000',
  MUSIC_SERVICE: 'https://music-service.mangovoice.co.uk',
  GATEWAY: 'https://customer-api.mangovoice.co.uk',
  WEB_APP: 'https://app.mangovoice.co.uk',
}

const api_env = getApiEnvironment()
const getDomain = (domain: keyof ApiDomains): string => {
  if (api_env === 'local') {
    return localDomains[domain] ?? productionDomains[domain]
  }

  if (api_env === 'staging') {
    return stagingDomains[domain] ?? productionDomains[domain]
  }

  if (api_env === 'production_uk') {
    return productionUKDomains[domain] ?? productionDomains[domain]
  }

  return productionDomains[domain]
}

export const domains: ApiDomainsAccessor = {
  JWT_AUTH: () => getDomain('JWT_AUTH'),
  MANGO_API: () => getDomain('MANGO_API'),
  CHAT_RTM: () => getDomain('CHAT_RTM'),
  MUSIC_SERVICE: () => getDomain('MUSIC_SERVICE'),
  GATEWAY: () => getDomain('GATEWAY'),
  WEB_APP: () => getDomain('WEB_APP'),
}
